import React from 'react';
import AnimateHeight from 'react-animate-height';

import SectionPanel from '../SectionPanel';
import {
  GroupContent,
  SubGroupContent,
  ItemList,
  ItemRow,
  SubgroupSectionPanel,
  SubGroupExpand,
  GroupFooter,
} from './styles';

export type GroupedData<T> = Record<string, T[]>;

export type SectionState = 'marked' | 'half-marked' | 'unmarked';

export interface SubGroupConfig {
  state?: SectionState;
  defaultExpanded?: boolean;
}

// State props
export interface CardGroupConfig<T> {
  data: T[] | GroupedData<T>;
  groupTitle: string;
  groupFeedback?: string;
  groupState?: SectionState;

  subgroupConfig?: Record<string, SubGroupConfig>;
  readOnly?: boolean;
}

// Component props (with render slots and event handlers)
export interface CardGroupProps<T> extends CardGroupConfig<T> {
  // Render functions
  renderItem: (
    item: T,
    subGroup: string | null,
    config: CardGroupConfig<T>,
  ) => React.ReactNode;
  renderSubgroupActions?: (
    subGroup: string,
    config: CardGroupConfig<T>,
  ) => React.ReactNode;
  renderFooter?: (config: CardGroupConfig<T>) => React.ReactNode;
  keyExtractor: (item: T, config: CardGroupConfig<T>) => React.Key;

  // Events
  onGroupClick?: (subGroup?: string) => void;
}

function isGroupedData<T>(data: T[] | GroupedData<T>): data is GroupedData<T> {
  return !Array.isArray(data);
}

function CardGroup<T>({
  // State props
  data,
  groupTitle,
  groupFeedback,
  groupState,
  subgroupConfig,
  readOnly = false,
  // Render props
  keyExtractor,
  renderItem,
  renderSubgroupActions,
  renderFooter,
  // Event handlers
  onGroupClick,
}: CardGroupProps<T>): React.ReactElement {
  const [expandedSubGroups, setExpandedSubGroups] = React.useState<
    Record<string, boolean>
  >({});

  const config: CardGroupConfig<T> = React.useMemo(
    () => ({
      data,
      groupTitle,
      groupFeedback,
      groupState,
      subgroupConfig,
      readOnly,
    }),
    [data, groupTitle, groupFeedback, groupState, subgroupConfig, readOnly],
  );

  const getSubGroupDefaultExpanded = React.useCallback(
    (subGroup: string) => subgroupConfig?.[subGroup]?.defaultExpanded ?? true,
    [subgroupConfig],
  );

  const getSubGroupState = React.useCallback(
    (subGroup: string) => subgroupConfig?.[subGroup]?.state,
    [subgroupConfig],
  );

  const isSubGroupExpanded = React.useCallback(
    (subGroup: string) =>
      expandedSubGroups[subGroup] ?? getSubGroupDefaultExpanded(subGroup),
    [expandedSubGroups, getSubGroupDefaultExpanded],
  );

  const handleToggleSubGroup = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>, subGroup: string) => {
      event.stopPropagation();
      setExpandedSubGroups(previous => ({
        ...previous,
        [subGroup]: !(
          previous[subGroup] ?? getSubGroupDefaultExpanded(subGroup)
        ),
      }));
    },
    [getSubGroupDefaultExpanded],
  );

  const renderItems = React.useCallback(
    (items: T[], subGroup: string | null) => (
      <ItemList>
        {items.map(item => (
          <ItemRow key={keyExtractor(item, config)}>
            {renderItem(item, subGroup, config)}
          </ItemRow>
        ))}
      </ItemList>
    ),
    [config, keyExtractor, renderItem],
  );

  return (
    <GroupContent>
      {!readOnly && (
        <SectionPanel
          title={groupTitle}
          feedback={groupFeedback}
          state={groupState}
          onClick={() => onGroupClick?.()}
        />
      )}

      {isGroupedData(data)
        ? Object.entries(data).map(([subGroupTitle, items]) =>
            subGroupTitle === '' ? (
              <React.Fragment key="__global__">
                {renderItems(items, null)}
              </React.Fragment>
            ) : (
              <SubGroupContent
                key={subGroupTitle}
                expanded={isSubGroupExpanded(subGroupTitle)}
              >
                <SubgroupSectionPanel
                  title={subGroupTitle}
                  state={getSubGroupState(subGroupTitle)}
                  onClick={() => onGroupClick?.(subGroupTitle)}
                  readOnly={readOnly}
                  Actions={
                    <>
                      {renderSubgroupActions?.(subGroupTitle, config)}
                      <SubGroupExpand
                        expanded={isSubGroupExpanded(subGroupTitle)}
                        onClick={event => {
                          handleToggleSubGroup(event, subGroupTitle);
                        }}
                      />
                    </>
                  }
                />
                <AnimateHeight
                  duration={350}
                  animateOpacity
                  style={{ marginLeft: '-24px', paddingLeft: '24px' }}
                  height={isSubGroupExpanded(subGroupTitle) ? 'auto' : 0}
                >
                  {renderItems(items, subGroupTitle)}
                </AnimateHeight>
              </SubGroupContent>
            ),
          )
        : renderItems(data, null)}

      {renderFooter && <GroupFooter>{renderFooter(config)}</GroupFooter>}
    </GroupContent>
  );
}

export default CardGroup;
